import { useState } from "react";
import {
  checkPermission,
  permissionType,
  hasAdminAccess,
  HR_ROLE_ID,
} from "../../../app/utils/common/Functions";
import { useDispatch, useSelector } from "react-redux";
import {
  addAdminHrLeaveNoteAPI,
  addLeaveCommentAPI,
  revokeApprovedLeaveAPI,
  reviewCancelApprovedLeaveAPI,
  sendEmailForLeaveRequestAPI,
} from "../../../Redux/Apis/leaveApi";
import { GoogleLoginFunction } from "../../../app/utils/common/GoogleAuth";
import AddButton from "../../atoms/AddButton/AddButton";
import { Modal } from "antd";
import {
  approverLeaveStatusBadgeClass,
  formatApproverLeaveStatusLabel,
} from "../../../app/utils/common/leaveStatusDisplay";

// Leave Details Drawer Component
export const LeaveApproverDetailsDrawer = ({ leave, onClose }) => {
  const [comment, setComment] = useState("");
  const [adminHrNote, setAdminHrNote] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showSendMailModal, setShowSendMailModal] = useState(false);
  const [emailComment, setEmailComment] = useState("");
  const [emailStatus, setEmailStatus] = useState("approved");
  const [emailError, setEmailError] = useState("");
  const [revokeComment, setRevokeComment] = useState("");
  const [hrCancelReviewComment, setHrCancelReviewComment] = useState("");
  const { user } = useSelector((state) => state.auth.auth);

  const dispatch = useDispatch();
  // console.log(leave);
  if (!leave) return null;

  // Check if current user is already in the approvers list
  const isUserAlreadyApproved = leave.approvers?.some(
    (approver) => approver.userId === user.id
  );

  const isHrRole = HR_ROLE_ID === Number(user?.roleId);
  const isAdminOnly = hasAdminAccess(user) && !isHrRole;
  const adminHrNotes = Array.isArray(leave?.adminHrNotes) ? leave.adminHrNotes : [];
  const hasAdminHrNote = Boolean(
    leave?.hasAdminHrNote || adminHrNotes.length > 0
  );
  const statusLabel = formatApproverLeaveStatusLabel(leave);
  const statusBadgeClass = approverLeaveStatusBadgeClass(leave);

  const showManagerDecision =
    checkPermission(permissionType.LEAVE_APPROVE, "approve") &&
    leave.status === "pending" &&
    !isHrRole &&
    !isAdminOnly &&
    !isUserAlreadyApproved &&
    !leave.managerStatus;

  const showAdminHrNoteForm =
    checkPermission(permissionType.LEAVE_APPROVE, "approve") &&
    leave.status === "pending" &&
    isAdminOnly &&
    !hasAdminHrNote;

  const canViewAdminHrNotes = isHrRole || isAdminOnly;
  const showAdminHrNotes =
    canViewAdminHrNotes && adminHrNotes.length > 0;

  const canFinalizeLeave = isHrRole || hasAdminAccess(user);

  const showFinalDecision =
    checkPermission(permissionType.LEAVE_APPROVE, "approve") &&
    leave.status === "pending" &&
    canFinalizeLeave &&
    !isUserAlreadyApproved;

  /** HR only: shown while waiting for an admin note (informational only). */
  const showHrAdminApprovalPending =
    isHrRole && showFinalDecision && !hasAdminHrNote;

  const cancelPending =
    leave.status === "approved" &&
    String(leave.cancelRequestStatus || "").toLowerCase() === "pending";

  const showHrCancelReview =
    checkPermission(permissionType.LEAVE_APPROVE, "approve") &&
    isHrRole &&
    cancelPending;

  const showHrDirectRevoke =
    checkPermission(permissionType.LEAVE_APPROVE, "approve") &&
    isHrRole &&
    leave.status === "approved" &&
    !cancelPending;

  const cancelledByComment = (() => {
    const cancellationComment = String(leave?.cancellationComment || "").trim();
    if (cancellationComment) return cancellationComment;
    const latestActionComment = String(leave?.latestActionComment || "").trim();
    if (latestActionComment) return latestActionComment;
    const approvers = Array.isArray(leave?.approvers) ? leave.approvers : [];
    const cancelledEntry = [...approvers]
      .reverse()
      .find((a) => String(a?.status || "").toLowerCase() === "cancelled");
    return String(cancelledEntry?.comment || "").trim();
  })();

  const formatDate = (dateString, timeString) => {
    if (!dateString) return "-";

    const date = new Date(dateString);
    const formattedDate = date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });

    if (timeString) {
      return `${formattedDate} at ${timeString}`;
    }

    return formattedDate;
  };

  const leaveDayKey = (value) => {
    if (!value) return null;
    const s = String(value).trim();
    const datePart = s.includes("T") ? s.split("T")[0] : s.slice(0, 10);
    return /^\d{4}-\d{2}-\d{2}$/.test(datePart) ? datePart : null;
  };
  const isSingleDayLeave =
    leaveDayKey(leave.startDate) &&
    leaveDayKey(leave.startDate) === (leaveDayKey(leave.endDate) || leaveDayKey(leave.startDate));
  const singleDayTimeRange = [leave.startTime, leave.endTime].filter(Boolean).join(" – ");

  const submitDecision = async (status) => {
    setActionLoading(true);
    try {
      await dispatch(
        addLeaveCommentAPI({
          status,
          comment,
          leaveRequestId: leave.leaveRequestId,
        })
      ).unwrap();
      onClose();
    } catch (_err) {
      // Error toast from leaveApproveReducer
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprove = () => submitDecision("approved");

  const handleReject = () => submitDecision("rejected");

  const handleSubmitAdminHrNote = async () => {
    const trimmed = adminHrNote.trim();
    if (!trimmed) return;
    setActionLoading(true);
    try {
      await dispatch(
        addAdminHrLeaveNoteAPI({
          leaveRequestId: leave.leaveRequestId,
          comment: trimmed,
        })
      ).unwrap();
      setAdminHrNote("");
      onClose();
    } catch (_err) {
      // Toast from leaveApproveReducer
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevokeApproved = async () => {
    setActionLoading(true);
    try {
      await dispatch(
        revokeApprovedLeaveAPI({
          leaveRequestId: leave.leaveRequestId,
          comment: revokeComment.trim() || undefined,
        })
      ).unwrap();
      setRevokeComment("");
      onClose();
    } catch (_err) {
      // Toast from leaveApproveReducer
    } finally {
      setActionLoading(false);
    }
  };

  const submitCancelReview = async (decision) => {
    setActionLoading(true);
    try {
      await dispatch(
        reviewCancelApprovedLeaveAPI({
          leaveRequestId: leave.leaveRequestId,
          decision,
          comment: hrCancelReviewComment.trim() || undefined,
        })
      ).unwrap();
      setHrCancelReviewComment("");
      onClose();
    } catch (_err) {
      // Toast from leaveApproveReducer
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenEmailModal = () => {
    setShowEmailModal(true);
    setEmailError("");
  };

  const handleCloseEmailModal = () => {
    setShowEmailModal(false);
    setEmailComment("");
    setEmailStatus("approved");
    setEmailError("");
  };
  const googleLogin = GoogleLoginFunction((data, error) => {
    if (data) {
      // Close the modal
      dispatch(
        sendEmailForLeaveRequestAPI({
          accessToken: data.accessToken,
          status: emailStatus,
          comment: emailComment,
          leaveRequestId: leave.leaveRequestId,
        })
      );
      handleCloseEmailModal();
      setShowSendMailModal(false);
      onClose();
    }
  });

  const handleSubmitEmail = () => {
    // Validate form
    if (!emailStatus) {
      setEmailError("Please select a status");
      return;
    }

    if (!emailComment.trim()) {
      setEmailError("Please add a comment");
      return;
    }
    googleLogin();
    // If validation passes, log the payload
  };

  return (
    <div className="fixed inset-0 overflow-hidden z-50 flex justify-end">
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      ></div>
      <div className="leave-approver-drawer-panel relative w-full max-w-md bg-white shadow-xl h-full overflow-y-auto">
        <div className="p-4 sm:p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-bold text-gray-900">
              Leave Request Details
            </h3>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 focus:outline-none"
            >
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* User Info */}
          <div className="flex items-center mb-6">
            <div className="h-14 w-14 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
              {leave.userProfileImage ? (
                <img
                  src={leave.userProfileImage}
                  alt={`${leave.userFirstname} ${leave.userLastname}`}
                  className="h-full w-full object-cover"
                />
              ) : (
                <span className="text-xl font-bold !text-black">
                  {leave.userFirstname?.charAt(0)}
                  {leave.userLastname?.charAt(0)}
                </span>
              )}
            </div>
            <div className="ml-4">
              <h4 className="text-lg font-semibold !text-black">{`${leave.userFirstname} ${leave.userLastname}`}</h4>
              <p className="text-gray-600">{leave.userEmail}</p>
            </div>
          </div>

          {/* Leave Details */}
          <div className="bg-blue-50 rounded-lg p-4 mb-6">
            <div className="mb-4">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Type:</span>
                <span className="text-sm font-medium !text-black">{leave.leaveType}</span>
              </div>
            </div>
            <div className="mb-4">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Category:</span>
                <span className="text-sm font-medium !text-black">
                  {leave.leaveCategory}
                </span>
              </div>
            </div>
            <div className="mb-4">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Status:</span>
                <span className={`text-sm font-medium px-2 py-1 rounded !text-black ${statusBadgeClass}`}>
                  {statusLabel}
                </span>
              </div>
            </div>
            {isSingleDayLeave ? (
              <div className="mb-4">
                <div className="flex justify-between gap-3">
                  <span className="text-sm text-gray-600">Date:</span>
                  <span className="text-sm font-medium !text-black text-right">
                    {formatDate(leave.startDate)}
                    {singleDayTimeRange ? ` · ${singleDayTimeRange}` : ""}
                  </span>
                </div>
              </div>
            ) : (
              <>
                <div className="mb-4">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">From:</span>
                    <span className="text-sm font-medium !text-black">
                      {formatDate(leave.startDate, leave.startTime)}
                    </span>
                  </div>
                </div>
                <div className="mb-4">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">To:</span>
                    <span className="text-sm font-medium !text-black">
                      {formatDate(leave.endDate, leave.endTime)}
                    </span>
                  </div>
                </div>
              </>
            )}
            <div className="mb-2">
              <span className="text-sm text-gray-600">Reason:</span>
              <p className="text-sm mt-1 p-2 bg-white rounded border border-gray-100 !text-black">
                {leave.reason || "No reason provided"}
              </p>
            </div>
            {cancelPending && (
              <div className="mt-3 p-2 bg-orange-50 rounded border border-orange-200">
                <span className="text-sm font-medium text-orange-900">Employee cancellation request</span>
                <p className="text-sm mt-1 !text-black">
                  {String(leave.cancelRequestComment || "").trim() || "—"}
                </p>
              </div>
            )}
            {leave.status === "cancelled" && (
              <div className="mt-3">
                <span className="text-sm text-gray-600">Cancellation reason:</span>
                <p className="text-sm mt-1 p-2 bg-white rounded border border-gray-100 !text-black">
                  {cancelledByComment || "No cancellation reason provided"}
                </p>
              </div>
            )}
          </div>
          {/* {leave.status === "pending" &&
            checkPermission(permissionType.LEAVE_APPROVE, "send mail") && (
              <div className="mb-6">
                <button
                  onClick={handleOpenEmailModal}
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-4 rounded-md transition duration-150 ease-in-out flex justify-center items-center"
                >
                  <svg
                    className="w-5 h-5 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    ></path>
                  </svg>
                  Send Mail
                </button>
              </div>
            )} */}
          {/* Send Email Button */}

          {/* Approval Action Section */}
          {showHrCancelReview && (
            <div className="mb-6 bg-orange-50 p-4 rounded-lg border border-orange-200">
              <h4 className="text-lg font-semibold mb-2 text-orange-900">Cancellation request</h4>
              <p className="text-sm text-orange-900 mb-3">
                The employee asked to cancel this approved leave. Approve to mark the leave as cancelled, or reject to keep it approved.
              </p>
              <label htmlFor="hr-cancel-review-comment" className="block text-sm font-medium text-gray-700 mb-1">
                Your comment (optional)
              </label>
              <textarea
                id="hr-cancel-review-comment"
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm mb-3 !text-black"
                placeholder="Note for the employee…"
                value={hrCancelReviewComment}
                onChange={(e) => setHrCancelReviewComment(e.target.value)}
              />
              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  onClick={() => submitCancelReview("approved")}
                  disabled={actionLoading}
                  className="w-full bg-green-700 hover:bg-green-800 text-white font-medium py-2 px-4 rounded-md"
                >
                  {actionLoading ? "Working…" : "Approve cancellation"}
                </button>
                <button
                  type="button"
                  onClick={() => submitCancelReview("rejected")}
                  disabled={actionLoading}
                  className="w-full bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-md"
                >
                  Reject cancellation
                </button>
              </div>
            </div>
          )}

          {showHrDirectRevoke && (
            <div className="mb-6 bg-amber-50 p-4 rounded-lg border border-amber-200">
              <h4 className="text-lg font-semibold mb-2 text-amber-900">Cancel approved leave (direct)</h4>
              <p className="text-sm text-amber-800 mb-3">
                This sets the request to <strong>Cancelled</strong> immediately, without an employee request. The record stays for audit.
              </p>
              <label htmlFor="revoke-comment" className="block text-sm font-medium text-gray-700 mb-1">
                Comment (optional)
              </label>
              <textarea
                id="revoke-comment"
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm mb-3 !text-black"
                placeholder="Reason for cancellation…"
                value={revokeComment}
                onChange={(e) => setRevokeComment(e.target.value)}
              />
              <button
                type="button"
                onClick={handleRevokeApproved}
                disabled={actionLoading}
                className="w-full bg-amber-700 hover:bg-amber-800 text-white font-medium py-2 px-4 rounded-md"
              >
                {actionLoading ? "Working…" : "Cancel this approved leave"}
              </button>
            </div>
          )}

          {showAdminHrNotes && (
            <div className="mb-6 bg-indigo-50 p-4 rounded-lg border border-indigo-200">
              <h4 className="text-lg font-semibold mb-2 text-indigo-900">
                {isHrRole ? "Admin note for HR" : "Admin note sent to HR"}
              </h4>
              <p className="text-sm text-indigo-800 mb-3">
                {isHrRole
                  ? "Internal note from leadership. Use this when drafting the employee email below."
                  : "This note was sent to HR. You can also finalize the request below if HR is unavailable."}
              </p>
              <div className="space-y-3">
                {adminHrNotes.map((note, index) => (
                  <div
                    key={`admin-hr-note-${index}`}
                    className="bg-white p-3 rounded-md border border-indigo-100"
                  >
                    <p className="text-xs font-medium text-gray-500 mb-1">
                      {`${note.firstname || ""} ${note.lastname || ""}`.trim() || "Admin"}
                    </p>
                    <p className="text-sm !text-black whitespace-pre-wrap">{note.comment}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {showAdminHrNoteForm && (
            <div className="mb-6 bg-violet-50 p-4 rounded-lg border border-violet-200">
              <h4 className="text-lg font-semibold mb-2 text-violet-900">Note for HR</h4>
              <p className="text-sm text-violet-800 mb-3">
                Add an internal comment for HR. Other admins can view this note. HR will be notified by email and will finalize the request.
              </p>
              <label htmlFor="admin-hr-note" className="block text-sm font-medium text-gray-700 mb-1">
                Your comment <span className="text-red-500">*</span>
              </label>
              <textarea
                id="admin-hr-note"
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm mb-3 !text-black"
                placeholder="Context or recommendation for HR…"
                value={adminHrNote}
                onChange={(e) => setAdminHrNote(e.target.value)}
              />
              <button
                type="button"
                onClick={handleSubmitAdminHrNote}
                disabled={actionLoading || !adminHrNote.trim()}
                className="w-full bg-violet-700 hover:bg-violet-800 disabled:opacity-50 text-white font-medium py-2 px-4 rounded-md"
              >
                {actionLoading ? "Sending…" : "Send note to HR"}
              </button>
            </div>
          )}

          {(showManagerDecision || showFinalDecision) && (
            <div className="mb-6 bg-gray-50 p-4 rounded-lg border border-gray-200">
              {showHrAdminApprovalPending && (
                <p className="mb-4 text-sm font-medium text-amber-900">
                  Admin comment pending
                </p>
              )}
              <h4 className="text-lg font-semibold mb-3 !text-black">
                {showFinalDecision
                  ? isHrRole
                    ? "Finalize (HR)"
                    : "Finalize (Admin)"
                  : "Your Decision"}
              </h4>

              <div className="mb-4">
                <label
                  htmlFor="comment"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Email Draft
                </label>
                <textarea
                  id="comment"
                  rows="3"
                  className="text-black w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter email draft here..."
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                ></textarea>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleApprove}
                  disabled={actionLoading}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md transition duration-150 ease-in-out flex justify-center items-center"
                >
                  {actionLoading ? (
                    <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                  ) : null}
                  Approve
                </button>
                <button
                  onClick={handleReject}
                  disabled={actionLoading}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-md transition duration-150 ease-in-out flex justify-center items-center"
                >
                  {actionLoading ? (
                    <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                  ) : null}
                  Reject
                </button>
              </div>
            </div>
          )}

          {checkPermission(permissionType.LEAVE_APPROVE, "approve") &&
            isUserAlreadyApproved &&
            !(showManagerDecision || showFinalDecision) && (
              <div className="mb-6 bg-blue-50 p-4 rounded-lg border border-blue-200">
                <div className="flex items-center">
                  <svg
                    className="w-5 h-5 text-blue-500 mr-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z"
                      clipRule="evenodd"
                    ></path>
                  </svg>
                  <p className="text-blue-700 font-medium">
                    You have already responded to this leave request.
                  </p>
                </div>
              </div>
            )}

          {/* Approvers Section with Better Comment Display */}
          <div className="mb-4">
            <h4 className="text-lg font-semibold mb-3 !text-black">Approvers History</h4>
            <div className="space-y-4">
              {leave.approvers?.map((approver, index) => (
                <div
                  key={index}
                  className={`border rounded-lg p-4 shadow-sm ${approver.status === "approved"
                    ? "border-l-4 border-l-green-500"
                    : approver.status === "rejected"
                      ? "border-l-4 border-l-red-500"
                      : approver.status === "cancelled"
                        ? "border-l-4 border-l-slate-500"
                        : "border-l-4 border-l-yellow-500"
                    }`}
                >
                  <div className="flex items-center mb-3">
                    <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden mr-3">
                      {approver.profileImage ? (
                        <img
                          src={approver.profileImage}
                          alt={approver.firstname}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <span className="text-sm font-bold text-gray-500">
                          {approver.firstname?.charAt(0)}
                          {approver.lastname?.charAt(0)}
                        </span>
                      )}
                    </div>
                    <div className="flex-1">
                      <h5 className="font-medium">{`${approver.firstname} ${approver.lastname}`}</h5>
                      <p className="text-xs text-gray-600">{approver.email}</p>
                    </div>
                    <div>
                      <span
                        className={`text-xs font-medium px-2 py-1 rounded ${approver.status === "approved"
                          ? "bg-green-100 text-green-800"
                          : approver.status === "rejected"
                            ? "bg-red-100 text-red-800"
                            : approver.status === "cancelled"
                              ? "bg-slate-100 text-slate-700"
                              : "bg-yellow-100 text-yellow-800"
                          }`}
                      >
                        {approver.status.charAt(0).toUpperCase() +
                          approver.status.slice(1)}
                      </span>
                    </div>
                  </div>
                  {approver.comment && (
                    <div className="bg-white p-3 rounded-md border border-gray-100 mt-2">
                      <p className="text-xs font-medium text-gray-500 mb-1">
                        Comment:
                      </p>
                      <p className="text-sm text-gray-700">
                        {approver.comment}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Email Modal */}
          {showEmailModal && (
            <div className="fixed inset-0 overflow-y-auto z-[60] flex items-center justify-center">
              <div
                className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
                onClick={handleCloseEmailModal}
              ></div>

              <div className="relative bg-white rounded-lg max-w-md w-full mx-auto shadow-xl p-6 z-[70]">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Email Notification
                  </h3>
                  <button
                    onClick={handleCloseEmailModal}
                    className="text-gray-400 hover:text-gray-500 focus:outline-none"
                  >
                    <svg
                      className="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>

                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-2">
                    Send email notification to {leave.userFirstname}{" "}
                    {leave.userLastname} regarding their leave request.
                  </p>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <div
                        className={`border rounded-md p-3 cursor-pointer flex items-center justify-center ${emailStatus === "approved"
                          ? "border-green-500 bg-green-50"
                          : "border-gray-300 hover:border-gray-400"
                          }`}
                        onClick={() => setEmailStatus("approved")}
                      >
                        <svg
                          className={`w-5 h-5 mr-2 ${emailStatus === "approved"
                            ? "text-green-600"
                            : "text-gray-400"
                            }`}
                          fill="currentColor"
                          viewBox="0 0 20 20"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          ></path>
                        </svg>
                        <span
                          className={`font-medium ${emailStatus === "approved"
                            ? "text-green-700"
                            : "text-gray-700"
                            }`}
                        >
                          Approve
                        </span>
                      </div>
                      <div
                        className={`border rounded-md p-3 cursor-pointer flex items-center justify-center ${emailStatus === "rejected"
                          ? "border-red-500 bg-red-50"
                          : "border-gray-300 hover:border-gray-400"
                          }`}
                        onClick={() => setEmailStatus("rejected")}
                      >
                        <svg
                          className={`w-5 h-5 mr-2 ${emailStatus === "rejected"
                            ? "text-red-600"
                            : "text-gray-400"
                            }`}
                          fill="currentColor"
                          viewBox="0 0 20 20"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            fillRule="evenodd"
                            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                            clipRule="evenodd"
                          ></path>
                        </svg>
                        <span
                          className={`font-medium ${emailStatus === "rejected"
                            ? "text-red-700"
                            : "text-gray-700"
                            }`}
                        >
                          Reject
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="mb-4">
                    <label
                      htmlFor="emailComment"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Comment <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="emailComment"
                      rows="4"
                      className={`w-full px-3 py-2 border ${emailError && !emailComment.trim()
                        ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                        : "border-gray-300 focus:ring-indigo-500 focus:border-indigo-500"
                        } rounded-md shadow-sm focus:outline-none`}
                      placeholder="Add your comment to include in the email..."
                      value={emailComment}
                      onChange={(e) => {
                        setEmailComment(e.target.value);
                        if (e.target.value.trim()) {
                          setEmailError("");
                        }
                      }}
                    ></textarea>
                  </div>
                </div>

                {/* Error message */}
                {emailError && (
                  <div className="mb-4 bg-red-50 border-l-4 border-red-500 p-3">
                    <div className="flex items-center">
                      <svg
                        className="h-5 w-5 text-red-500 mr-2"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <p className="text-sm text-red-700">{emailError}</p>
                    </div>
                  </div>
                )}
                <div>
                  <AddButton
                    className={"w-full flex justify-center items-center"}
                    buttonTitle="Send"
                    onClick={() => setShowSendMailModal(true)}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <Modal
        title="Send Mail"
        open={showSendMailModal}
        onOk={handleSubmitEmail}
        onCancel={() => setShowSendMailModal(false)}
        okText="Yes, Send"
        cancelText="Cancel"
        okButtonProps={{
          danger: true,
        }}
      >
        <p>
          Are you sure you want to send mail to {leave.userFirstname}{" "}
          {leave.userLastname}?
        </p>
      </Modal>
    </div>
  );
};
